import React, { useState, useEffect } from 'react';
import {
  FiPlus, FiSearch, FiFilter, FiEdit, FiTrash2, FiCopy,
  FiCalendar, FiDownload, FiEye, FiClock, FiTrendingUp, FiChevronDown, FiChevronUp, FiUserPlus, FiUsers, FiX
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import programService from '../../services/programService';
import ProgramWizard from './ProgramWizard';
import MultiClientAssignmentModal from './MultiClientAssignmentModal';
import './Programs.css';

const Programs = () => {
  const { currentUser, userProfile } = useAuth();

  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all'); // 'all', 'my_programs', 'favorites', 'draft', 'complete'
  const [showFilters, setShowFilters] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [editingProgramId, setEditingProgramId] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [expandedDrafts, setExpandedDrafts] = useState(true);
  const [expandedComplete, setExpandedComplete] = useState(true);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [programToAssign, setProgramToAssign] = useState(null);
  const [showAssignedClientsModal, setShowAssignedClientsModal] = useState(false);
  const [viewingAssignedProgram, setViewingAssignedProgram] = useState(null);

  useEffect(() => {
    loadPrograms();
  }, [currentUser]);

  useEffect(() => {
    applyFilters();
  }, [programs, searchTerm, selectedCategory, selectedDifficulty, selectedStatus]);

  const loadPrograms = async () => {
    setLoading(true);
    try {
      // Get coach's draft programs + all completed programs from any coach
      const loadedPrograms = await programService.getProgramsForCoach(currentUser.uid);
      setPrograms(loadedPrograms);
    } catch (error) {
      console.error('[Programs] Error loading programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = programService.searchPrograms(programs, searchTerm, {
      category: selectedCategory,
      difficulty: selectedDifficulty
    });

    // Filter by status
    if (selectedStatus === 'my_programs') {
      filtered = filtered.filter(p => p.coachId === currentUser.uid);
    } else if (selectedStatus === 'favorites') {
      filtered = filtered.filter(p => p.metadata?.isFavorite === true);
    } else if (selectedStatus !== 'all') {
      filtered = filtered.filter(p => p.status === selectedStatus);
    }

    setFilteredPrograms(filtered);
  };

  // Separate programs by status
  const draftPrograms = filteredPrograms.filter(p => p.status === 'draft');
  const completePrograms = filteredPrograms.filter(p => p.status === 'complete' || !p.status); // Handle legacy programs without status

  const handleCreateProgram = () => {
    setEditingProgramId(null);
    setShowWizard(true);
  };

  const handleEditProgram = (programId) => {
    setEditingProgramId(programId);
    setShowWizard(true);
  };

  const handleDuplicateProgram = async (programId) => {
    try {
      await programService.duplicateProgram(programId, currentUser.uid);
      loadPrograms();
      alert('Program duplicated successfully!');
    } catch (error) {
      console.error('[Programs] Error duplicating program:', error);
      alert('Error duplicating program');
    }
  };

  const handleDeleteProgram = async (programId) => {
    if (!window.confirm('Are you sure you want to delete this program?')) return;

    try {
      await programService.deleteProgram(programId);
      setPrograms(programs.filter(p => p.id !== programId));
      alert('Program deleted successfully!');
    } catch (error) {
      console.error('[Programs] Error deleting program:', error);
      alert('Error deleting program');
    }
  };

  const handleProgramSaved = (savedProgram) => {
    loadPrograms();
    setShowWizard(false);
  };

  const handleAssignToClients = (program) => {
    setProgramToAssign(program);
    setShowAssignmentModal(true);
  };

  const handleViewAssignedClients = (program) => {
    setViewingAssignedProgram(program);
    setShowAssignedClientsModal(true);
  };

  const handleClientsAssigned = async (selectedClients) => {
    try {
      const assignmentData = {
        programId: programToAssign.id,
        programName: programToAssign.name || programToAssign.metadata?.name,
        coachId: currentUser.uid,
        coachName: userProfile?.displayName || 'Unknown Coach',
        assignedAt: new Date().toISOString(),
        numberOfCycles: programToAssign.numberOfCycles || programToAssign.metadata?.duration?.weeks || 0,
        sessionsPerCycle: programToAssign.sessionsPerCycle || programToAssign.metadata?.duration?.sessionsPerWeek || 0,
        status: 'active'
      };

      // Create assignments in each client's subcollection
      for (const client of selectedClients) {
        const clientAssignment = {
          ...assignmentData,
          clientId: client.id,
          clientName: client.name,
          startDate: client.startDate
        };

        // Add to client's assignedPrograms subcollection
        await programService.assignProgramToClient(client.id, programToAssign.id, clientAssignment);

        // Create calendar entry for client
        await programService.createCalendarEntry(client.id, {
          type: 'program_start',
          title: `Start: ${assignmentData.programName}`,
          date: client.startDate,
          programId: programToAssign.id,
          coachId: currentUser.uid,
          coachName: assignmentData.coachName
        });

        // Create calendar entry for trainer
        await programService.createCalendarEntry(currentUser.uid, {
          type: 'program_start',
          title: `${client.name} starts: ${assignmentData.programName}`,
          date: client.startDate,
          programId: programToAssign.id,
          clientId: client.id,
          clientName: client.name
        });
      }

      // Update program metadata with assignment summary
      const assignmentSummary = selectedClients.map(client => ({
        clientId: client.id,
        clientName: client.name,
        startDate: client.startDate,
        assignedAt: new Date().toISOString()
      }));

      await programService.updateProgram(programToAssign.id, {
        'metadata.assignedClients': assignmentSummary,
        'metadata.lastAssignedAt': new Date().toISOString()
      });

      setShowAssignmentModal(false);
      setProgramToAssign(null);
      alert(`Program assigned to ${selectedClients.length} client${selectedClients.length !== 1 ? 's' : ''} successfully!`);
      loadPrograms();
    } catch (error) {
      console.error('[Programs] Error assigning program to clients:', error);
      alert('Error assigning program. Please try again.');
    }
  };

  const categories = programService.getProgramCategories();

  // Render a program card
  const renderProgramCard = (program) => {
    const isNewStructure = program.cycles && program.sessionsPerCycle;

    return (
      <div key={program.id} className="program-card">
        <div className="program-header">
          <div className="program-title-section">
            <div className="program-title-row">
              <h3>{program.name || program.metadata?.name}</h3>
              {program.status === 'draft' && <span className="draft-badge-small">Draft</span>}
            </div>
            {program.creatorName && (
              <div className="program-creator">
                <span className="creator-label">Created by:</span>
                <span className="creator-name">{program.creatorName}</span>
              </div>
            )}
            <div className="program-tags">
              <span className={`category-tag ${program.groupType || program.metadata?.category}`}>
                {(program.groupType || program.metadata?.category || 'general').replace('_', ' ')}
              </span>
              {program.metadata?.difficulty && (
                <span className={`difficulty-tag ${program.metadata.difficulty}`}>
                  {program.metadata.difficulty}
                </span>
              )}
            </div>
          </div>
          <div className="program-header-right">
            <span className="times-used-badge">
              {program.metadata?.timesUsed || 0} times used
            </span>
            <div className="program-action-icons">
              <button
                onClick={() => handleEditProgram(program.id)}
                title={program.status === 'draft' ? 'Continue editing' : 'Edit program'}
              >
                <FiEdit />
              </button>
              <button
                onClick={() => handleDuplicateProgram(program.id)}
                title="Duplicate program"
                disabled={program.status === 'draft'}
              >
                <FiCopy />
              </button>
              <button
                onClick={() => handleDeleteProgram(program.id)}
                title="Delete program"
                className="delete"
              >
                <FiTrash2 />
              </button>
            </div>
          </div>
        </div>

        {(program.metadata?.description || program.description) && (
          <p className="program-description">{program.metadata?.description || program.description}</p>
        )}

        <div className="program-stats">
          {isNewStructure ? (
            <>
              <div className="stat">
                <FiClock />
                <span>{program.numberOfCycles} cycles</span>
              </div>
              <div className="stat">
                <FiCalendar />
                <span>{program.sessionsPerCycle}x per cycle</span>
              </div>
              <div className="stat">
                <FiTrendingUp />
                <span>{program.daysPerCycle} days/cycle</span>
              </div>
            </>
          ) : (
            <>
              <div className="stat">
                <FiClock />
                <span>{program.metadata?.duration?.weeks || 0} weeks</span>
              </div>
              <div className="stat">
                <FiCalendar />
                <span>{program.metadata?.duration?.sessionsPerWeek || 0}x/week</span>
              </div>
              <div className="stat">
                <FiTrendingUp />
                <span>{program.workouts?.length || 0} workouts</span>
              </div>
            </>
          )}
        </div>

        {program.metadata?.targetClient && (
          <div className="program-client">
            <span>For: {program.metadata.targetClient.clientName}</span>
          </div>
        )}

        {program.metadata?.goals && program.metadata.goals.length > 0 && (
          <div className="program-goals">
            {program.metadata.goals.slice(0, 3).map((goal, index) => (
              <span key={index} className="goal-tag">{goal}</span>
            ))}
            {program.metadata.goals.length > 3 && (
              <span className="more-goals">+{program.metadata.goals.length - 3} more</span>
            )}
          </div>
        )}

        <div className="program-footer">
          <div className="program-footer-left">
            {program.metadata?.assignedClients && program.metadata.assignedClients.length > 0 && (
              <div
                className="assigned-clients-indicator"
                onClick={() => handleViewAssignedClients(program)}
                title="View assigned clients"
              >
                <FiUsers />
                <span className="client-count">{program.metadata.assignedClients.length}</span>
              </div>
            )}
            {program.metadata?.lastUsed && (
              <span className="last-used">
                Used {new Date(program.metadata.lastUsed.toDate()).toLocaleDateString()}
              </span>
            )}
          </div>
          {program.status !== 'draft' && (
            <div className="program-quick-actions">
              <button className="quick-action" title="View program">
                <FiEye /> View
              </button>
              <button className="quick-action" title="Export PDF">
                <FiDownload /> Export
              </button>
              <button
                onClick={() => handleAssignToClients(program)}
                className="quick-action assign-action"
                title="Assign to clients"
              >
                <FiUserPlus /> Assign to Clients
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (showWizard) {
    return (
      <ProgramWizard
        programId={editingProgramId}
        onClose={(savedProgram) => {
          if (savedProgram) {
            loadPrograms();
          }
          setShowWizard(false);
        }}
      />
    );
  }

  return (
    <div className="programs-container">
      {/* Header */}
      <div className="programs-header">
        <div className="header-left">
          <h2>Training Programs</h2>
          <p className="subtitle">{programs.length} program{programs.length !== 1 ? 's' : ''} created</p>
        </div>
        <button className="create-program-btn" onClick={handleCreateProgram}>
          <FiPlus /> Create Program
        </button>
      </div>

      {/* Search and Filters */}
      <div className="search-filters-bar">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search programs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <button
          className="filters-toggle"
          onClick={() => setShowFilters(!showFilters)}
        >
          <FiFilter /> Filters
        </button>

        <div className="view-toggle">
          <button
            className={viewMode === 'grid' ? 'active' : ''}
            onClick={() => setViewMode('grid')}
          >
            Grid
          </button>
          <button
            className={viewMode === 'list' ? 'active' : ''}
            onClick={() => setViewMode('list')}
          >
            List
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">All Programs</option>
              <option value="my_programs">My Programs</option>
              <option value="favorites">Favorites</option>
              <option value="draft">Drafts Only</option>
              <option value="complete">Complete Only</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Difficulty</label>
            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
            >
              <option value="all">All Levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
              <option value="expert">Expert</option>
            </select>
          </div>
        </div>
      )}

      {/* Programs List/Grid */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading programs...</p>
        </div>
      ) : filteredPrograms.length === 0 ? (
        <div className="empty-state">
          {programs.length === 0 ? (
            <>
              <h3>No Programs Yet</h3>
              <p>Create your first training program to get started</p>
              <button className="create-first-btn" onClick={handleCreateProgram}>
                <FiPlus /> Create First Program
              </button>
            </>
          ) : (
            <>
              <h3>No Programs Found</h3>
              <p>Try adjusting your search or filters</p>
            </>
          )}
        </div>
      ) : (
        <>
          {/* Draft Programs Section */}
          {draftPrograms.length > 0 && (
            <div className="programs-section">
              <div className="section-header" onClick={() => setExpandedDrafts(!expandedDrafts)}>
                <div className="section-title">
                  <h3>Draft Programs</h3>
                  <span className="section-count">{draftPrograms.length}</span>
                </div>
                <button className="section-toggle">
                  {expandedDrafts ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              </div>
              {expandedDrafts && (
                <div className={`programs-${viewMode}`}>
                  {draftPrograms.map(renderProgramCard)}
                </div>
              )}
            </div>
          )}

          {/* Complete Programs Section */}
          {completePrograms.length > 0 && (
            <div className="programs-section">
              <div className="section-header" onClick={() => setExpandedComplete(!expandedComplete)}>
                <div className="section-title">
                  <h3>Complete Programs</h3>
                  <span className="section-count">{completePrograms.length}</span>
                </div>
                <button className="section-toggle">
                  {expandedComplete ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              </div>
              {expandedComplete && (
                <div className={`programs-${viewMode}`}>
                  {completePrograms.map(renderProgramCard)}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Multi-Client Assignment Modal */}
      {showAssignmentModal && programToAssign && (
        <MultiClientAssignmentModal
          program={programToAssign}
          onAssign={handleClientsAssigned}
          onClose={() => {
            setShowAssignmentModal(false);
            setProgramToAssign(null);
          }}
        />
      )}

      {/* Assigned Clients View Modal */}
      {showAssignedClientsModal && viewingAssignedProgram && (
        <div className="assigned-clients-modal">
          <div className="modal-overlay" onClick={() => setShowAssignedClientsModal(false)}></div>
          <div className="modal-content assigned-clients-content">
            <div className="modal-header">
              <h3>Clients Using This Program</h3>
              <button className="close-btn" onClick={() => setShowAssignedClientsModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="program-title-display">
                <strong>{viewingAssignedProgram.name || viewingAssignedProgram.metadata?.name}</strong>
              </div>
              <div className="assigned-clients-list">
                {viewingAssignedProgram.metadata?.assignedClients?.map((client, index) => (
                  <div key={index} className="assigned-client-item">
                    <div className="client-avatar-small">
                      {client.clientName.charAt(0).toUpperCase()}
                    </div>
                    <div className="client-info-display">
                      <div className="client-name-display">{client.clientName}</div>
                      <div className="client-details-display">
                        <span>Start: {new Date(client.startDate).toLocaleDateString()}</span>
                        <span className="separator">•</span>
                        <span>Assigned: {new Date(client.assignedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Programs;
